// The app's view of the Stytch session. One wrapper so no component outside
// this feature imports `@stytch/react` — swapping the provider stays a
// one-directory change.
import { useStytch, useStytchSession, useStytchUser } from "@stytch/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { clearDrafts } from "@/shared/lib/drafts";

import type { StytchClient } from "../client";

export interface SessionState {
  /** True once the SDK has read persistent storage — render nothing before it. */
  isInitialized: boolean;
  isAuthenticated: boolean;
}

export function useSession(): SessionState {
  const { session, isInitialized } = useStytchSession();
  return { isInitialized, isAuthenticated: !!session };
}

/** The signed-in account's id (Stytch's), whose drafts the composer keeps; null when signed out. */
export function useSignedInUserId(): string | null {
  return useStytchUser().user?.user_id ?? null;
}

/** The signed-in account's email, for "Signed in as"; null when signed out. */
export function useSignedInEmail(): string | null {
  return useStytchUser().user?.emails?.[0]?.email ?? null;
}

/** The client the provider was given. Stories and tests hand in a fake. */
export const useStytchClient = (): StytchClient => useStytch() as StytchClient;

/**
 * Sign out from inside the app: revoke, drop every cached server response, then
 * route to `to` (`/login`) without a reload. The module-level `signOut` in
 * `../session` is the same thing for callers with no React context.
 */
export function useSignOut(to = "/login"): () => Promise<void> {
  const client = useStytchClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(async () => {
    // A failed revoke changes nothing: we clear the cache and leave for /login
    // either way, and a session with no client to use it is already inert.
    await client.session.revoke().catch(() => undefined);
    queryClient.clear();
    clearDrafts(); // what this person typed and didn't send stays theirs
    navigate(to, { replace: true });
  }, [client, queryClient, navigate, to]);
}
